import React, { useState, useEffect } from 'react';
import { permissionsAPI } from '../../utils/api';
import AdminHeader from './AdminHeader';
import './PermissionManagement.css';

function PermissionManagement({ onNavigate, currentView, embedded = false }) {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissionsByCategory, setPermissionsByCategory] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // New role creation state
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [newRole, setNewRole] = useState({
    name: '',
    displayName: '',
    description: '',
    color: '#3498db',
    priority: 0
  });

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const response = await permissionsAPI.getAllRoles();
      setRoles(response.data.roles);
      setError(null);
    } catch (err) {
      console.error('Permission API Error:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Unknown error';
      setError('Failed to load roles: ' + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const loadRolePermissions = async (roleId) => {
    try {
      setLoading(true);
      const response = await permissionsAPI.getRoleById(roleId);
      setSelectedRole(response.data.role);
      setPermissionsByCategory(response.data.permissionsByCategory);
      setError(null);
    } catch (err) {
      setError('Failed to load role permissions: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = (role) => {
    loadRolePermissions(role.id);
  };

  const handlePermissionToggle = (permissionName) => {
    // Update the permission state
    const updatedCategories = { ...permissionsByCategory };

    for (const category in updatedCategories) {
      const permission = updatedCategories[category].find(p => p.name === permissionName);
      if (permission) {
        permission.enabled = !permission.enabled;
        break;
      }
    }

    setPermissionsByCategory(updatedCategories);
  };

  const handleSavePermissions = async () => {
    if (!selectedRole) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Collect all enabled permissions
      const enabledPermissions = [];
      for (const category in permissionsByCategory) {
        permissionsByCategory[category].forEach(perm => {
          if (perm.enabled) {
            enabledPermissions.push(perm.name);
          }
        });
      }

      await permissionsAPI.updateRolePermissions(selectedRole.id, enabledPermissions);

      setSuccess('Permissions updated successfully!');
      loadRoles(); // Refresh role list
    } catch (err) {
      setError('Failed to save permissions: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      await permissionsAPI.createRole(newRole);
      setSuccess('Role created successfully!');
      setShowCreateRole(false);
      setNewRole({ name: '', displayName: '', description: '', color: '#3498db', priority: 0 });
      loadRoles();
    } catch (err) {
      setError('Failed to create role: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;

    try {
      setSaving(true);
      setError(null);
      await permissionsAPI.deleteRole(roleId);
      setSuccess('Role deleted successfully!');
      setSelectedRole(null);
      loadRoles();
    } catch (err) {
      setError('Failed to delete role: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading && !selectedRole) {
    return <div className="permission-loading">Loading permissions...</div>;
  }

  return (
    <>
      {!embedded && <AdminHeader currentView={currentView} onNavigate={onNavigate} />}
      <div className="permission-management">
      {!embedded && <div className="permission-header">
        <h1>Permission Management</h1>
        <button
          className="create-role-btn"
          onClick={() => setShowCreateRole(!showCreateRole)}
        >
          {showCreateRole ? 'Cancel' : '+ Create Role'}
        </button>
      </div>}

      {error && <div className="permission-error">{error}</div>}
      {success && <div className="permission-success">{success}</div>}

      {showCreateRole && (
        <div className="create-role-form">
          <h2>Create New Role</h2>
          <form onSubmit={handleCreateRole}>
            <div className="form-group">
              <label>Role Name (lowercase, no spaces):</label>
              <input
                type="text"
                value={newRole.name}
                onChange={(e) => setNewRole({...newRole, name: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                required
                placeholder="e.g., security_analyst"
              />
            </div>
            <div className="form-group">
              <label>Display Name:</label>
              <input
                type="text"
                value={newRole.displayName}
                onChange={(e) => setNewRole({...newRole, displayName: e.target.value})}
                required
                placeholder="e.g., Security Analyst"
              />
            </div>
            <div className="form-group">
              <label>Description:</label>
              <textarea
                value={newRole.description}
                onChange={(e) => setNewRole({...newRole, description: e.target.value})}
                placeholder="Role description..."
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Color:</label>
                <input
                  type="color"
                  value={newRole.color}
                  onChange={(e) => setNewRole({...newRole, color: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label>Priority (0-1000):</label>
                <input
                  type="number"
                  value={newRole.priority}
                  onChange={(e) => setNewRole({...newRole, priority: parseInt(e.target.value)})}
                  min="0"
                  max="1000"
                />
              </div>
            </div>
            <button type="submit" disabled={saving}>
              {saving ? 'Creating...' : 'Create Role'}
            </button>
          </form>
        </div>
      )}

      <div className="permission-content">
        <div className="roles-sidebar">
          <h2>Roles ({roles.length})</h2>
          <div className="roles-list">
            {roles.map(role => (
              <div
                key={role.id}
                className={`role-item ${selectedRole?.id === role.id ? 'active' : ''}`}
                onClick={() => handleRoleSelect(role)}
              >
                <div className="role-info">
                  <div className="role-name" style={{ color: role.color }}>
                    {role.displayName}
                    {role.isSystemRole && <span className="system-badge">System</span>}
                  </div>
                  <div className="role-meta">
                    {role.permissionCount} permissions
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="permissions-panel">
          {selectedRole ? (
            <>
              <div className="role-header">
                <div>
                  <h2 style={{ color: selectedRole.color }}>{selectedRole.displayName}</h2>
                  <p className="role-description">{selectedRole.description}</p>
                  <div className="role-stats">
                    <span>Priority: {selectedRole.priority}</span>
                    <span>{selectedRole.permissions.length} permissions assigned</span>
                  </div>
                </div>
                <div className="role-actions">
                  <button
                    className="save-btn"
                    onClick={handleSavePermissions}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  {!selectedRole.isSystemRole && (
                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteRole(selectedRole.id)}
                      disabled={saving}
                    >
                      Delete Role
                    </button>
                  )}
                </div>
              </div>

              {selectedRole.isSystemRole && (
                <div className="warning-box">
                  ℹ️ This is a system role. Changes will affect all users with this role.
                </div>
              )}

              <div className="permissions-matrix">
                {Object.entries(permissionsByCategory).map(([category, permissions]) => (
                  <div key={category} className="permission-category">
                    <h3 className="category-title">{category.toUpperCase()}</h3>
                    <div className="permission-grid">
                      {permissions.map(permission => (
                        <label
                          key={permission.name}
                          className={`permission-checkbox ${permission.enabled ? 'enabled' : ''}`}
                        >
                          <input
                            type="checkbox"
                            checked={permission.enabled}
                            onChange={() => handlePermissionToggle(permission.name)}
                            disabled={false}
                          />
                          <div className="permission-info">
                            <div className="permission-name">{permission.displayName}</div>
                            <div className="permission-desc">{permission.description}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="no-role-selected">
              <p>← Select a role from the sidebar to manage permissions</p>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

export default PermissionManagement;
